import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { config } from 'config';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { createHttpParams } from '../helpers/utils';

export interface Setting<T = string> {
  systemsettingid: number;
  systemsettingcategoryid: string;
  settingkeyname: string;
  settingvalue: T;
  createddate: string;
  createdby: string;
  lastupdated: string;
  lastupdatedby: string;
  expirydate: string;
  expiredby: string;
  systemsettingstatus: number;
  settingimage: string;
  settingimage_s3: string;
  isUpdating?: boolean; // Added for frontend purpose to track whether the setting is being updated
}
export interface SettingsResponse {
  settings: {
    [key: string]: Setting;
  };
}

export interface SystemSettingCategory {
  category_id: number;
  category_name: string;
}

export interface SystemSettingsCategoriesPayload {
  category_id?: number;
  editable?: number | null;
  search_term?: string;
}

export interface SystemSettingsCategoriesResponse {
  categories: {
    [key: number]: SystemSettingCategory;
  };
  status: number;
}

export interface UpdateSystemSettingPayload {
  setting_id: number;
  setting_value: string | null;
}

export interface GetSystemSettingsPayload {
  categoryid?: number;
  editable?: 0 | 1;
  keyname?: string;
}

export interface GetAllocationStatusResponse {
  data: AllocationStatus[];
}

export interface AllocationStatus {
  type: string;
  status: number;
  msg?: string;
}

export interface StatusCodeConfig {
  name: string;
  colour: string;
  profile_type_id?: string; // Comma-separated list of profile type IDs that can see this status
}

export interface StatusCodesResponse {
  [key: string]: StatusCodeConfig;
}

export interface FilterOption {
  label: string;
  value: string;
  is_active: boolean;
}

export interface PortalFilterConfiguration {
  service_types?: FilterOption[];
  appointment_types?: FilterOption[];
  consult_statuses?: FilterOption[];
}

export interface GetPortalFilterConfigPayload {
  keyname: string;
  Source: string;
}

@Injectable({
  providedIn: 'root',
})
export class SystemSettingsService {
  private cachedFilterConfiguration: BehaviorSubject<PortalFilterConfiguration | null> = new BehaviorSubject<PortalFilterConfiguration | null>(null);

  constructor(private http: HttpClient) { }

  getSystemSettings(
    payload: GetSystemSettingsPayload
  ): Observable<SettingsResponse['settings']> {
    const params = createHttpParams(payload);

    return this.http
      .get<SettingsResponse>(config.apiUrl + 'getSystemSettings', {
        params,
        responseType: 'json',
      })
      .pipe(
        map(data => data.settings),
        catchError(err => throwError(err))
      );
  }

  getSystemSettingsCategories(payload?: SystemSettingsCategoriesPayload) {
    const params = createHttpParams(payload);

    return this.http
      .get<SystemSettingsCategoriesResponse>(
        config.apiUrl1 + 'get_system_setting_categories',
        {
          params,
        }
      )
      .pipe(
        map(data => data.categories),
        catchError(err => throwError(err))
      );
  }

  updateSystemSetting(payload: UpdateSystemSettingPayload) {
    return this.http
      .post<{ msg: string; status: number }>(config.apiUrl1 + 'update_system_setting', payload)
      .pipe(catchError(err => throwError(err)));
  }

  getAutoAllocationData(): Observable<GetAllocationStatusResponse> {
    const url = config.apiUrl2 + 'cron/get_job_action';
    return this.http.get<GetAllocationStatusResponse>(url);
  }

  updateAutoAllocation(payload: AllocationStatus) {
    const url = config.apiUrl2 + 'cron/add_job_action';
    return this.http.post<AllocationStatus>(url, payload);
  }

  /**
   * Get status codes filtered by user role
   * Filters based on the 'profile_type_id' property in each status code configuration
   * @param profileTypeId The user's profile type ID to check against
   */
  getRoleBasedStatusCodes(profileTypeId: number): Observable<StatusCodesResponse> {
    return this.getSystemSettings({ keyname: 'status_codes' }).pipe(
      map(settings => {
        const statusCodesSettings = settings['status_codes'];
        if (!statusCodesSettings?.settingvalue) {
          return {};
        }

        let statusCodes: StatusCodesResponse;
        try {
          statusCodes = JSON.parse(statusCodesSettings.settingvalue);
        } catch (error) {
          console.error('Error parsing status codes:', error);
          return {};
        }

        // Filter status codes based on user profile type
        const filteredStatusCodes: StatusCodesResponse = {};

        Object.entries(statusCodes).forEach(([code, config]) => {
          // If no profile_type_id property is set, show to all users
          if (!config.profile_type_id) {
            filteredStatusCodes[code] = config;
            return;
          }

          // Check if user's profile type ID is in the allowed profile types
          const allowedProfileTypes = config.profile_type_id.split(',').map(id => parseInt(id.trim()));
          if (allowedProfileTypes.includes(profileTypeId)) {
            filteredStatusCodes[code] = config;
          }
        });

        return filteredStatusCodes;
      }),
      catchError(err => {
        // console.error('Error fetching role-based status codes:', err);
        return throwError(err);
      })
    );
  }

  getPortalFilterConfiguration(): Observable<PortalFilterConfiguration> {
    // Return cached data if available
    if (this.cachedFilterConfiguration.value !== null) {
      return this.cachedFilterConfiguration.asObservable().pipe(take(1));
    }

    const params = createHttpParams({
      categoryid: config.env === 'production' ? 68 : 71,
    });

    return this.http
      .get<SettingsResponse>(config.apiUrl + 'getSystemSettings', {
        params,
        responseType: 'json',
      })
      .pipe(
        map(data => {
          const config: PortalFilterConfiguration = {
            service_types: [],
            appointment_types: [],
            consult_statuses: []
          };

          // Parse service_types
          const serviceTypesSetting = data.settings['service_types'];
          if (serviceTypesSetting?.settingvalue) {
            try {
              config.service_types = JSON.parse(serviceTypesSetting.settingvalue);
            } catch (e) {
              console.error('Error parsing service_types:', e);
            }
          }

          // Parse appointment_types
          const appointmentTypesSetting = data.settings['appointment_types'];
          if (appointmentTypesSetting?.settingvalue) {
            try {
              config.appointment_types = JSON.parse(appointmentTypesSetting.settingvalue);
            } catch (e) {
              console.error('Error parsing appointment_types:', e);
            }
          }

          // Parse consult_statuses
          const consultStatusesSetting = data.settings['consult_statuses'];
          if (consultStatusesSetting?.settingvalue) {
            try {
              config.consult_statuses = JSON.parse(consultStatusesSetting.settingvalue);
            } catch (e) {
              console.error('Error parsing consult_statuses:', e);
            }
          }

          return config;
        }),
        tap(config => {
          this.cachedFilterConfiguration.next(config);
        }),
        switchMap(() => this.cachedFilterConfiguration.asObservable().pipe(take(1))),
        catchError(err => {
          console.error('Error fetching portal filter configuration:', err);
          return throwError(err);
        })
      );
  }
}
